import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  PackagePlus,
  Eye,
  Edit,
  Trash2,
  XCircle,
  MapPin,
  Calendar,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent, Badge } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { CancelOrderDialog } from '@components/CancelOrderDialog';
import { formatDateBR } from '@pages/financial/financialUtils';
import { formatCepDisplay } from '@/utils/addressCepCheck';
import { goodsDeliveryOrderService } from '../../services/goodsDeliveryOrderService';
import {
  GoodsDeliveryOrder,
  GoodsDeliveryOrderSearchModel,
  GoodsDeliveryOrderStatusFilter,
} from '../../types/goodsDeliveryOrder';
import {
  canDeleteDeliveryOrder,
  DELIVERY_ORDER_STATUS_LABELS,
  deliveryOrderStatus,
  deliveryOrderStatusBadge,
  isDeliveryOrderOpen,
  parseEmergencyQuery,
} from './goodsDeliveryOrderUtils';

type StatusFilterValue = 'all' | GoodsDeliveryOrderStatusFilter;

const PAGE_SIZE = 50;

export const GoodsDeliveryOrderListView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [orders, setOrders] = useState<GoodsDeliveryOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<StatusFilterValue>('all');
  // O sino abre /goods-delivery-orders?emergency=1 com o filtro já ligado.
  const [onlyEmergency, setOnlyEmergency] = useState(() => parseEmergencyQuery(location.search));
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);

  const [deleteTarget, setDeleteTarget] = useState<GoodsDeliveryOrder | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<GoodsDeliveryOrder | null>(null);

  // Nova navegação do sino com a tela já aberta: reaplica o filtro da URL.
  useEffect(() => {
    setOnlyEmergency(parseEmergencyQuery(location.search));
    setPage(1);
  }, [location.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const searchModel: GoodsDeliveryOrderSearchModel = {
        page,
        pageSize: PAGE_SIZE,
        searchName: debouncedSearch || undefined,
        status: filterStatus === 'all' ? undefined : filterStatus,
        is_emergency: onlyEmergency || undefined,
      };
      const response = await goodsDeliveryOrderService.getOrders(searchModel);
      setOrders(response.data);
      setTotalOrders(response.pagination.total || response.data.length);
      setTotalPages(Math.max(1, response.pagination.totalPages));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar ordens de entrega');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, filterStatus, onlyEmergency]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await goodsDeliveryOrderService.deleteOrder(deleteTarget.goods_delivery_orders_id);
      setDeleteTarget(null);
      await loadOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir ordem de entrega');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = async (reason: string) => {
    if (!cancelTarget) return;
    await goodsDeliveryOrderService.cancelOrder(cancelTarget.goods_delivery_orders_id, reason);
    setCancelTarget(null);
    await loadOrders();
  };

  const toggleEmergency = (checked: boolean) => {
    setOnlyEmergency(checked);
    setPage(1);
    // Mantém a URL coerente com o filtro (e permite compartilhar o link).
    const params = new URLSearchParams(location.search);
    if (checked) params.set('emergency', '1');
    else params.delete('emergency');
    const qs = params.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  };

  const hasFilters = !!debouncedSearch || filterStatus !== 'all' || onlyEmergency;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-h1 mb-2">Ordens de Entrega</h1>
          <p className="text-slate-600">Entregas avulsas de mercadorias (fora de pedido de venda e convite)</p>
        </div>
        <PermissionGuard permission="goods_delivery_orders.create">
          <Button icon={<Plus size={20} />} onClick={() => navigate('/goods-delivery-orders/new')}>
            Nova Ordem de Entrega
          </Button>
        </PermissionGuard>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por descrição, destinatário ou endereço..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                startIcon={<Search size={20} />}
              />
            </div>
            <select
              className="input-field text-sm md:w-48"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value as StatusFilterValue); setPage(1); }}
            >
              <option value="all">Todos</option>
              <option value="pending">Pendentes</option>
              <option value="delivered">Entregues</option>
              <option value="canceled">Canceladas</option>
            </select>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 whitespace-nowrap">
              <input
                type="checkbox"
                className="rounded border-slate-300 text-red-600 focus:ring-red-500"
                checked={onlyEmergency}
                onChange={(e) => toggleEmergency(e.target.checked)}
              />
              Somente emergência
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader title={`Lista de Ordens de Entrega (${totalOrders})`} />
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-500">Carregando ordens de entrega...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <PackagePlus className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={loadOrders} variant="outline" size="sm">
                Tentar novamente
              </Button>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-8">
              <PackagePlus className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">
                {hasFilters ? 'Nenhuma ordem encontrada com os filtros atuais' : 'Nenhuma ordem de entrega cadastrada'}
              </p>
              {!hasFilters && (
                <PermissionGuard permission="goods_delivery_orders.create">
                  <Button onClick={() => navigate('/goods-delivery-orders/new')}>Criar primeira ordem de entrega</Button>
                </PermissionGuard>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <tr>
                    <th className="text-left">#</th>
                    <th className="text-left">Descrição</th>
                    <th className="text-left">Destinatário / Endereço</th>
                    <th className="text-left">Data prevista</th>
                    <th className="text-center">Status</th>
                    <th className="text-left">Solicitante</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {orders.map((order) => {
                    const status = deliveryOrderStatus(order);
                    const open = isDeliveryOrderOpen(order);
                    return (
                      <TableRow key={order.goods_delivery_orders_id}>
                        <TableCell>
                          <span className="font-mono text-sm text-slate-600">#{order.goods_delivery_orders_id}</span>
                        </TableCell>

                        <TableCell>
                          <div className="max-w-xs">
                            <p className="font-medium text-slate-900 truncate">{order.description}</p>
                            {order.is_emergency && (
                              <Badge variant="danger" size="sm" className="mt-1 gap-1">
                                <AlertTriangle size={10} /> Emergência
                              </Badge>
                            )}
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex items-start gap-1">
                            <MapPin size={12} className="mt-0.5 text-slate-400 shrink-0" />
                            <div className="text-sm">
                              <p className="text-slate-900 font-medium">{order.recipient_name}</p>
                              <p className="text-xs text-slate-600">
                                {order.ship_addr_street}, {order.ship_addr_num}
                              </p>
                              <p className="text-xs text-slate-500">
                                {order.ship_addr_city}/{order.ship_addr_state} — CEP {formatCepDisplay(order.ship_addr_zip)}
                              </p>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Calendar size={12} className="text-slate-400" />
                            <span className="text-sm">{formatDateBR(order.expected_delivery_date) || '—'}</span>
                          </div>
                        </TableCell>

                        <TableCell className="text-center">
                          <Badge variant={deliveryOrderStatusBadge(status)}>{DELIVERY_ORDER_STATUS_LABELS[status]}</Badge>
                        </TableCell>

                        <TableCell>
                          <span className="text-sm text-slate-600">{order.requestor_email || order.created_by || '—'}</span>
                        </TableCell>

                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <PermissionGuard permission="goods_delivery_orders.read">
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Eye size={16} />}
                                onClick={() => navigate(`/goods-delivery-orders/${order.goods_delivery_orders_id}`)}
                                className="px-2"
                                title="Visualizar"
                              />
                            </PermissionGuard>

                            {open && (
                              <>
                                <PermissionGuard permission="goods_delivery_orders.write">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    icon={<Edit size={16} />}
                                    onClick={() => navigate(`/goods-delivery-orders/${order.goods_delivery_orders_id}/edit`)}
                                    className="px-2"
                                    title="Editar"
                                  />
                                </PermissionGuard>
                                <PermissionGuard permission="goods_delivery_orders.write">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    icon={<XCircle size={16} />}
                                    onClick={() => setCancelTarget(order)}
                                    className="px-2 text-amber-600 hover:text-amber-700"
                                    title="Cancelar ordem"
                                  />
                                </PermissionGuard>
                              </>
                            )}

                            {canDeleteDeliveryOrder(order) && (
                              <PermissionGuard permission="goods_delivery_orders.delete">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  icon={<Trash2 size={16} />}
                                  onClick={() => setDeleteTarget(order)}
                                  className="px-2 text-red-500 hover:text-red-700"
                                  title="Excluir"
                                />
                              </PermissionGuard>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    Anterior
                  </Button>
                  <span className="text-sm text-slate-500">
                    Pág {page}/{totalPages}
                  </span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                    Próxima
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Ordem de Entrega"
        message={`Deseja realmente excluir a ordem #${deleteTarget?.goods_delivery_orders_id ?? ''}? A parada ainda não entregue também sai da rota. Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        severity="danger"
        loading={deleting}
      />

      <CancelOrderDialog
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        title="Cancelar Ordem de Entrega"
        subject={`a ordem de entrega #${cancelTarget?.goods_delivery_orders_id ?? ''}`}
        routeWarning="Se estiver em uma rota ainda não entregue, a parada será removida da rota."
      />
    </div>
  );
};
